import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/apiConfig";

// V7.89 — Access Control. The effective key→bool map for the logged-in employee comes from
// the Primary's backend (/settings/permissions/mine), which serves the matrix the Primary
// pulled from Central (see backend/access_catalog.py for every key). Module-level cache so
// dozens of <Can> wrappers share ONE request per login instead of each refetching.
let cache = { id: null, promise: null, data: null };

function loadFor(employee) {
  const id = `${employee?.id}:${employee?.role}`;
  if (cache.id === id && cache.promise) return cache.promise;
  const token = localStorage.getItem("token");
  const promise = axios.get(`${API}/settings/permissions/mine`, {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then((res) => { cache.data = res.data || {}; return cache.data; })
    .catch(() => { cache.data = {}; return cache.data; });
  cache = { id, promise, data: null };
  return promise;
}

export function invalidatePermissionsCache() { cache = { id: null, promise: null, data: null }; }

export function readStoredEmployee() {
  try { return JSON.parse(localStorage.getItem("employee") || "null"); } catch { return null; }
}

export function usePermissions(employee) {
  const [permissions, setPermissions] = useState(cache.data);
  const hasEmployee = !!employee;
  const employeeId = employee?.id;
  const employeeRole = employee?.role;
  const [loading, setLoading] = useState(!cache.data);

  useEffect(() => {
    let active = true;
    if (!hasEmployee) { setLoading(false); return; }
    loadFor(employee).then((d) => { if (active) { setPermissions(d); setLoading(false); } });
    return () => { active = false; };
  }, [hasEmployee, employeeId, employeeRole]);

  // Tech Support always sees everything. While loading (or on failure) default to allowed so
  // the UI doesn't flash-hide — the backend enforces the sensitive actions independently.
  const can = useCallback((key) => {
    if (employee?.is_technician || employee?.id === "technician") return true;
    if (!permissions) return true;
    return permissions[key] !== false;
  }, [permissions, employee?.is_technician, employee?.id]);

  return { can, loading, permissions };
}

export default usePermissions;
